import React from 'react';
import FormattedDateTime from '../common/datetime/FormattedDateTime';
import StructuredMessage from './StructuredMessage';
import {
  BridgeApiAttachment,
  BridgeApiConversationCounterparty,
  BridgeApiConversationRequestedFields,
  BridgeApiTemplate,
  BridgeApiTemplateField,
  InternalApiAlertRfiMessage,
  InternalApiAlertRfiMessageType,
} from '../api-internal';
import {
  getBooleanFieldValueDisplayName,
  getDependentFields,
  getMessengerFieldConfiguration,
  getNameForCounterparty,
  getTemplateFieldDisplayMappings,
  onDownloadAttachment,
} from './helper';
import './Message.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faLock } from '@fortawesome/pro-solid-svg-icons';

export enum JsonMessageTypeEnum {
  STRUCTURED_REQUEST = 'STRUCTURED_REQUEST',
  STRUCTURED_REQUEST_RESPONSE = 'STRUCTURED_REQUEST_RESPONSE',
  CONCLUSION_SENDER = 'CONCLUSION_SENDER',
  CONCLUSION_RECIPIENT = 'CONCLUSION_RECIPIENT',
}

export interface JsonMessage {
  type: JsonMessageTypeEnum;
  message: string;
  attributes: { [key: string]: any };
  responseComment?: string;
}

export const getStructuredMessage = (message?: string): JsonMessage | undefined => {
  if (message) {
    try {
      const structuredMessage: JsonMessage = JSON.parse(message);

      if (Object.values(JsonMessageTypeEnum).includes(structuredMessage.type)) {
        return structuredMessage;
      }
    } catch (_) {
      // ignored
    }
  }

  return undefined;
};

const Message = ({
  message,
  template,
  customFields,
  counterparties,
  requestedFields,
}: {
  message: InternalApiAlertRfiMessage;
  template?: BridgeApiTemplate;
  customFields: BridgeApiTemplateField[];
  counterparties: BridgeApiConversationCounterparty[];
  requestedFields?: BridgeApiConversationRequestedFields;
}): React.JSX.Element => {
  const typeClass = message.type === InternalApiAlertRfiMessageType.RECEIVED ? 'message-received' : 'message-sent';

  const structuredMessage: JsonMessage | undefined = getStructuredMessage(message.content);

  // No need to show attachments that are shown in structured message
  const structuredMessageAttachmentIds = structuredMessage
    ? Object.values(structuredMessage.attributes)
        .filter((val) => Array.isArray(val))
        .map((val) => val.map((fileVal: { id: string }) => fileVal.id))
        .flat()
    : [];

  const requestedFieldsMappings = template?.requestedFields?.length
    ? getTemplateFieldDisplayMappings(template.requestedFields)
    : null;

  const hasRequestedFields =
    (requestedFields?.custom && requestedFields.custom.length > 0) ||
    (requestedFields?.fromTemplate && requestedFields.fromTemplate.length > 0);

  return (
    <div className={typeClass + ' message'} data-testid='message-container'>
      <div className='message-header text-s'>
        <span>
          {getNameForCounterparty(
            message.type,
            {
              userName: message.createdByUserName,
              organisationName: message.createdByOrganisationName,
            },
            counterparties
          )}
        </span>

        <span> • </span>

        <FormattedDateTime date={message.creationDate} showTime />
        {message.type === InternalApiAlertRfiMessageType.INTERNAL && (
          <>
            <span> • </span>
            <FontAwesomeIcon icon={faLock} className='me-2' />
            <span>Only visible to {message.createdByOrganisationName}</span>
          </>
        )}
      </div>

      <div
        className={`message-content ${message.type.toLowerCase()} ${
          message.content === 'DECRYPTION_FAILED' ? 'encrypted' : ''
        }`}
        data-testid='message-content'
      >
        {structuredMessage ? (
          <StructuredMessage jsonMessage={structuredMessage} template={template} customFields={customFields} />
        ) : (
          <div className='text-s'>{message.content}</div>
        )}

        {hasRequestedFields && requestedFieldsMappings && (
          <div className='requested-fields-area m-t-16 p-t-4 text-secondary'>
            <div className='text-strong-s'>Requested fields</div>
            <div className='text-xs'>
              {requestedFields.fromTemplate?.map((fieldName) => {
                const dependentRequestedFields = getDependentFields(template!);
                return (
                  requestedFieldsMappings[fieldName] && (
                    <div key={fieldName}>
                      {requestedFieldsMappings[fieldName].displayName}

                      {dependentRequestedFields[fieldName]?.map((dependentField) => {
                        const requiredValue = getMessengerFieldConfiguration(dependentField).show_when?.field_value;
                        const requiredAnswer =
                          typeof requiredValue === 'boolean'
                            ? getBooleanFieldValueDisplayName(requiredValue, requestedFieldsMappings[fieldName])
                            : requiredValue;

                        return (
                          <div key={dependentField.name} className='m-l-8'>
                            - {dependentField.displayName} (if
                            <span className='fw-bold'>{' ' + requiredAnswer}</span> is selected)
                          </div>
                        );
                      })}
                    </div>
                  )
                );
              })}

              {requestedFields.custom?.map(
                (field) => field.displayName && <div key={field.displayName}>{field.displayName}</div>
              )}
            </div>
          </div>
        )}

        <div className='attachments'>
          {message.attachments &&
            message.attachments
              .filter((a) => !structuredMessageAttachmentIds.includes(a.handle))
              .map((attachment: BridgeApiAttachment) => {
                return (
                  <div
                    className='attachment'
                    onClick={() => onDownloadAttachment(attachment.handle, attachment.fileName)}
                    key={attachment.handle}
                  >
                    {attachment.fileName}
                  </div>
                );
              })}
        </div>
      </div>
    </div>
  );
};

export default Message;
